// Size/colour option handling for products on an express-cart link.
//
// Option data completeness varies by integration (see
// nile-ai-mastra SPEED-CHECKOUT-CART-REVIEW.md §6.4), so axes are resolved in order:
//   1. `products.options`            e.g. [{ name: "Size", values: ["S","M"] }]
//   2. `variants[].options`          e.g. [{ name: "Size", value: "M" }]
//   3. variant titles split on " / " e.g. "M / Black"
//   4. a single "Option" axis of variant titles

export interface VariantOptionValue {
  name: string;
  value: string;
}

export interface ProductVariant {
  sku: string;
  title: string;
  price?: number | string | null;
  stock?: number | string | null;
  image?: string | null;
  options?: VariantOptionValue[] | null;
  /** Shopify variant GID, e.g. "gid://shopify/ProductVariant/123" (Shopify-synced brands). */
  shopify_variant_id?: string | null;
  /** Older field name some integrations used for the same ID. */
  shopify_id?: string | null;
}

export interface ProductOption {
  name: string;
  values: string[];
}

export interface CheckoutProduct {
  id: string;
  /** Base catalog SKU, required by Magento's configurable add-to-cart endpoint. */
  sku?: string | null;
  fulfillment_brand_id?: string | null;
  fulfillment_brand_name?: string | null;
  retailer_id?: string | null;
  retailer_name?: string | null;
  title: string;
  price_cents: number;
  currency: string;
  image_url: string | null;
  inventory_count: number | null;
  track_inventory: boolean | null;
  /** Shopify variant GID of the default variant (products without variants). */
  shopify_variant_id: string | null;
  variants: ProductVariant[];
  options: ProductOption[] | null;
}

export interface OptionAxis {
  name: string;
  hasImages: boolean;
  values: OptionValue[];
}

export interface OptionValueImage {
  value: string;
  image_url: string;
}

export type OptionValue = string | OptionValueImage;

interface ResolvedOptionAxis {
  name: string;
  values: string[];
}

/** Selected value per axis name, e.g. { Size: "M", Colour: "Black" }. */
export type OptionSelection = Record<string, string>;

export function toInt(value: number | string | null | undefined): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = typeof value === "number" ? value : Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}

/** Stock for a variant. Missing stock counts as 0, matching `get_cart`. */
export function variantStock(variant: ProductVariant): number {
  return Math.max(0, toInt(variant.stock) ?? 0);
}

export function sellableVariants(product: CheckoutProduct): ProductVariant[] {
  return (product.variants ?? []).filter((v) => Boolean(v?.sku));
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function titleParts(title: string): string[] {
  return title.split(" / ").map((part) => part.trim());
}

type AxisSource = "options" | "variant-options" | "title" | "flat";

function resolveAxes(product: CheckoutProduct): { axes: ResolvedOptionAxis[]; source: AxisSource } {
  const variants = sellableVariants(product);
  if (variants.length === 0) return { axes: [], source: "flat" };

  const declared = (product.options ?? []).filter((o) => o?.name && o.values?.length);
  if (declared.length > 0 && variants.every((v) => v.options?.length)) {
    return { axes: declared.map((o) => ({ name: o.name, values: unique(o.values) })), source: "options" };
  }

  if (variants.every((v) => v.options?.length)) {
    const names = unique(variants.flatMap((v) => v.options!.map((o) => o.name)));
    return {
      axes: names.map((name) => ({
        name,
        values: unique(variants.flatMap((v) => v.options!.filter((o) => o.name === name).map((o) => o.value))),
      })),
      source: "variant-options",
    };
  }

  const partCounts = unique(variants.map((v) => String(titleParts(v.title).length)));
  if (partCounts.length === 1 && Number(partCounts[0]) > 1) {
    const count = Number(partCounts[0]);
    const names = declared.length === count ? declared.map((o) => o.name) : Array.from({ length: count }, (_, i) => `Option ${i + 1}`);
    return {
      axes: names.map((name, i) => ({ name, values: unique(variants.map((v) => titleParts(v.title)[i] ?? "")) })),
      source: "title",
    };
  }

  return { axes: [{ name: declared[0]?.name ?? "Option", values: unique(variants.map((v) => v.title)) }], source: "flat" };
}

export function getOptionAxes(product: CheckoutProduct): OptionAxis[] {
  const { axes } = resolveAxes(product);
  const variants = sellableVariants(product).filter((variant) => variantStock(variant) > 0);

  return axes.map((axis) => {
    const imageByValue = new Map<string, Set<string>>();
    for (const variant of variants) {
      const value = variantValues(product, variant)[axis.name];
      if (value === undefined) continue;
      const image = variant.image?.trim() || product.image_url?.trim();
      if (!image) continue;
      const images = imageByValue.get(value) ?? new Set<string>();
      images.add(image);
      imageByValue.set(value, images);
    }

    const images = axis.values.map((value) => imageByValue.get(value));
    const hasImages = images.length > 1
      && images.every((valueImages) => valueImages?.size === 1)
      && new Set(images.map((valueImages) => [...valueImages!][0])).size > 1;

    return {
      name: axis.name,
      hasImages,
      values: hasImages
        ? axis.values.map((value) => ({ value, image_url: [...imageByValue.get(value)!][0]! }))
        : axis.values,
    };
  });
}

const SIZE_RANKS: string[][] = [
  ["xxs", "2xs", "extra extra small"],
  ["xs", "extra small"],
  ["s", "small"],
  ["regular", "standard", "std"],
  ["m", "medium"],
  ["l", "large"],
  ["xl", "extra large"],
  ["xxl", "2xl", "extra extra large"],
  ["xxxl", "3xl", "extra extra extra large"],
];

function normalizeSize(value: string): string {
  return value.toLowerCase().trim().replace(/[\s-]+/g, " ");
}

function compareAlphanumeric(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

/** Sort size aliases conventionally; unknown labels follow in alphanumeric order. */
export function sortOptionValues(values: string[]): string[] {
  const rankByAlias = new Map(SIZE_RANKS.flatMap((aliases, rank) =>
    aliases.map((alias) => [alias, rank] as const),
  ));
  return [...values].sort((a, b) => {
    const rankA = rankByAlias.get(normalizeSize(a));
    const rankB = rankByAlias.get(normalizeSize(b));
    if (rankA !== undefined && rankB !== undefined) {
      return rankA - rankB || compareAlphanumeric(a, b);
    }
    if (rankA !== undefined) return -1;
    if (rankB !== undefined) return 1;
    return compareAlphanumeric(a, b);
  });
}

/** The option values a variant has, keyed by axis name. */
export function variantValues(product: CheckoutProduct, variant: ProductVariant): OptionSelection {
  const { axes, source } = resolveAxes(product);
  const values: OptionSelection = {};

  axes.forEach((axis, i) => {
    let value: string | undefined;
    if (source === "options" || source === "variant-options") {
      value = variant.options?.find((o) => o.name === axis.name)?.value;
    } else if (source === "title") {
      value = titleParts(variant.title)[i];
    } else {
      value = variant.title;
    }
    if (value !== undefined) values[axis.name] = value;
  });

  return values;
}

function matches(product: CheckoutProduct, variant: ProductVariant, selection: OptionSelection): boolean {
  const values = variantValues(product, variant);
  return Object.entries(selection).every(([name, value]) => values[name] === value);
}

/** The variant that matches every axis, or null while the selection is incomplete. */
export function findVariant(product: CheckoutProduct, selection: OptionSelection): ProductVariant | null {
  const axes = getOptionAxes(product);
  if (axes.length === 0 || axes.some((axis) => !selection[axis.name])) return null;
  return sellableVariants(product).find((v) => matches(product, v, selection)) ?? null;
}

export type ValueState = "available" | "sold-out" | "unavailable";

/**
 * How a value relates to the shopper's other choices:
 * - available:   an in-stock variant exists with this value + other selections
 * - sold-out:    every variant it could lead to has no stock (not selectable)
 * - unavailable: not made in this combination, but in stock with other
 *                choices; picking it clears the conflicting ones
 */
export function valueState(
  product: CheckoutProduct,
  selection: OptionSelection,
  axisName: string,
  value: string,
): ValueState {
  const others = Object.fromEntries(Object.entries(selection).filter(([name]) => name !== axisName));
  const variants = sellableVariants(product);
  const inCombination = variants.filter((v) => matches(product, v, { ...others, [axisName]: value }));

  if (inCombination.length > 0) {
    return inCombination.some((v) => variantStock(v) > 0) ? "available" : "sold-out";
  }
  const anywhere = variants.filter((v) => matches(product, v, { [axisName]: value }));
  return anywhere.some((v) => variantStock(v) > 0) ? "unavailable" : "sold-out";
}

/**
 * Applies a new choice and drops any other choices that no longer form a
 * real variant together with it (e.g. switching colour clears a size that
 * doesn't exist in that colour).
 */
export function selectValue(
  product: CheckoutProduct,
  selection: OptionSelection,
  axisName: string,
  value: string,
): OptionSelection {
  const next: OptionSelection = { [axisName]: value };
  for (const [name, current] of Object.entries(selection)) {
    if (name === axisName) continue;
    const candidate = { ...next, [name]: current };
    if (sellableVariants(product).some((v) => matches(product, v, candidate))) next[name] = current;
  }
  return next;
}

/** Pre-selects products that only have one sellable variant. */
export function initialSelection(product: CheckoutProduct): OptionSelection {
  const [only, ...rest] = sellableVariants(product);
  return only && rest.length === 0 ? variantValues(product, only) : {};
}
